/**
 * Какие экраны кабинета открывает партнёр: `POST /api/partner/events`.
 *
 * Владельцу это нужно, чтобы видеть живые и мёртвые разделы («Отчёты → Партнёры»).
 * Шлём шаблон роута (`/bots/$botId`), а не адрес: id ботов и клиентов в аналитике
 * не нужны. Пачкой раз в несколько секунд, через keepalive, ошибки глушим: аналитика
 * не имеет права мешать кабинету. Копия есть во втором кабинете, правьте обе.
 */
import { useEffect } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { API_BASE, currentToken } from './api';

const APP = 'miniapp';
const FLUSH_MS = 5000;
const BATCH = 20;
const SAME_SCREEN_MS = 30_000;

let queue: Array<{ route: string; ts: number }> = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let last = { route: '', at: 0 };

function flush(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  const token = currentToken();
  if (!queue.length) return;
  // Токена ещё нет (вход не закончился) или он протух: подождём, а не отправим 401.
  if (!token) {
    timer = setTimeout(flush, FLUSH_MS);
    return;
  }
  const screens = queue.slice(0, BATCH);
  queue = queue.slice(BATCH);
  void fetch(`${API_BASE}/api/partner/events`, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ app: APP, screens }),
  }).catch(() => undefined);
  if (queue.length) timer = setTimeout(flush, FLUSH_MS);
}

function track(route: string): void {
  const now = Date.now();
  // Перерисовка того же экрана и возврат на него через секунду это не новый заход.
  if (route === last.route && now - last.at < SAME_SCREEN_MS) return;
  last = { route, at: now };
  queue.push({ route, ts: now });
  if (queue.length > BATCH * 3) queue = queue.slice(-BATCH * 3);
  if (queue.length >= BATCH) flush();
  else if (!timer) timer = setTimeout(flush, FLUSH_MS);
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
  // iOS и вебвью Telegram при закрытии часто шлют только pagehide.
  window.addEventListener('pagehide', flush);
}

/** Подключить один раз в корне кабинета. */
export function useScreenTracking(enabled = true): void {
  const routeId = useRouterState({
    select: (s) => s.matches[s.matches.length - 1]?.routeId ?? '',
  });
  useEffect(() => {
    if (!enabled || !routeId || routeId === '__root__') return;
    track(routeId.length > 1 ? routeId.replace(/\/$/, '') : routeId);
  }, [routeId, enabled]);
}
